import { useBreadcrumbStore } from "@lib/stores/breadcrumbStore";
import { useEffect } from "react";
import { useSearchParams } from "react-router";
import { ApprovalQueueTab } from "./ApprovalQueueTab";
import { AttentionTab } from "./AttentionTab";
import { InboxTab } from "./InboxTab";
import { ProgrammesTab } from "./ProgrammesTab";
import { ProspectsTab } from "./ProspectsTab";
import { TriageTab } from "./TriageTab";

const TABS = [
  { id: "approval", label: "Approval Queue" },
  { id: "inbox", label: "Inbox" },
  { id: "triage", label: "Triage" },
  { id: "prospects", label: "Prospects" },
  { id: "programmes", label: "Programmes" },
  { id: "attention", label: "Attention" },
] as const;

type TabId = (typeof TABS)[number]["id"];

export function OutreachPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get("tab") as TabId) || "approval";
  const programmeId = searchParams.get("programme");

  const setTail = useBreadcrumbStore((s) => s.setTail);
  useEffect(() => {
    setTail("Outreach");
    return () => setTail(null);
  }, [setTail]);

  const setTab = (id: TabId) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", id);
    setSearchParams(next, { replace: true });
  };

  const setProgramme = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) {
      next.set("programme", id);
    } else {
      next.delete("programme");
    }
    setSearchParams(next, { replace: true });
  };

  return (
    <div>
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text-primary">Outreach</h1>
          <p className="mt-1 text-sm text-text-muted">
            LinkedIn buying-intent signals, prospect triage, and approval queue
          </p>
        </div>
      </header>

      <nav className="mt-4 flex gap-1 border-b border-border">
        {TABS.map((t) => {
          const active = activeTab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                active
                  ? "border-accent text-text-primary"
                  : "border-transparent text-text-muted hover:text-text-secondary"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </nav>

      {programmeId && activeTab !== "programmes" && (
        <div className="mt-3 flex items-center gap-2 rounded-md bg-card-alt px-3 py-2 text-xs text-text-muted">
          <span>Scoped to programme</span>
          <button
            onClick={() => setProgramme(null)}
            className="rounded-full bg-card px-2 py-0.5 text-text-secondary hover:bg-card-hover"
          >
            Clear
          </button>
        </div>
      )}

      <div className="mt-5">
        {activeTab === "approval" && <ApprovalQueueTab programmeId={programmeId} />}
        {activeTab === "inbox" && <InboxTab programmeId={programmeId} />}
        {activeTab === "triage" && <TriageTab />}
        {activeTab === "prospects" && <ProspectsTab />}
        {activeTab === "programmes" && (
          <ProgrammesTab selectedProgrammeId={programmeId} onSelectProgramme={setProgramme} />
        )}
        {activeTab === "attention" && <AttentionTab />}
      </div>
    </div>
  );
}
